import { Box, Grid, Stack, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import React, { useContext, useEffect, useState } from 'react';
import { EmissionsEmailCampaignContentForm } from '../../../../presentation-components/emissons-components/survey/emissions-email-campaign-content-form';
import { EmissionsEmailCampaignScheduleForm } from '../../../../presentation-components/emissons-components/survey/emissions-email-campaign-schedule-form';
import { EmissionsEmailCampaignRecipientsForm } from '../../../../presentation-components/emissons-components/survey/emissions-email-campaign-recipients-form';
import { UserNotificationsContext } from '../../../../../context/user-notifications/user-notifications';
import EmailCampaignService from '../../../../../modules/survey/shared-services/email-campaign/email-campaign.service';
import { IEmailCampaign, IPostEmailCampaign } from '../../../../../modules/survey/shared-services/email-campaign/email-campaign.model';
import gaiaTheme from '../../../../../root-components/theme';
import { ReportPeriodContext } from '../../../../../context/report-periods/report-periods';
import { EmailCampaignEmployeeCommuteForm } from '../../../../../modules/survey/components/employee/email-campaign-employee-commute-form';
import DistributionListAddressService from '../../../../../shared/services/distribution-list-address/distribution-list-address.service';
import { IPostDistributionListAddress } from '../../../../../shared/services/distribution-list-address/distribution-list-address.model';
import { a11yProps } from '../../../../../shared/utils/misc-shared-utils';
import { CustomTabPanel } from '../../../../presentation-components/display-elements-components/custom-tab-panel';
import { GraphDonutChart } from '../../../../presentation-components/analytics-components/graphs/graph-donut-chart';
import AnalyticsService from '../../../../../shared/services/analytics/analytics.service';
import { InformationBox } from '../../../../presentation-components/miscellaneous-components/information-box';
import { IconName } from '../../../../../shared/interfaces/icon-picker.model';
import { GraphBarChart } from '../../../../presentation-components/analytics-components/graphs/graph-bar-chart';
import { SeriesBar } from '../../../../../shared/interfaces/analytics';
import { getMixedColorPalette } from '../../../../../shared/utils/colors-utils';
import { EmissionsEmailCampaignReviewForm } from '../../../../presentation-components/emissons-components/survey/emissions-email-campaign-review-form';
import { useReportPeriodWatcher } from '../../../../../hooks/use-report-period-watcher';
import { PageOuterContainer } from '../../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../../presentation-components/display-elements-components/page-heading';
import { useIsReportPeriodLocked } from '../../../../../hooks/use-is-report-period-locked';

export const EmissionsSurveyOverviewPage = () => {
  const { id } = useParams();
  const reportPeriodWatcher = useReportPeriodWatcher({ redirectUrl: `/emissions/surveys/employee` })
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams()
  //Services
  const emailCampaignService = new EmailCampaignService();
  const analyticService = new AnalyticsService()
  const distributionListAddressService = new DistributionListAddressService();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const reportPeriodContext = useContext(ReportPeriodContext);
  //State
  const [campaignId, setCampaignId] = useState<string>('')
  const [emailCampaign, setEmailCampaign] = useState<IEmailCampaign>()
  const [newSurvey, setNewSurvey] = useState<IPostEmailCampaign>()
  const [isSubmitted, setIsSubmitted] = useState<number>(0)
  const [value, setValue] = React.useState(0)
  const [analytics, setAnalytics] = useState<any>();
  const [emailAddresses, setEmailAddresses] = useState<IPostDistributionListAddress[]>()
  //Hooks
  const isReportPeriodLocked = useIsReportPeriodLocked()
  //Loading
  const [isChangingStatus, setIsChangingStatus] = useState<boolean>(false)
  const [isEditPageFormSubmitting, setIsEditPageFormSubmitting] = useState<boolean>(false)
  //Colors
  const theme = useTheme();
  const colors = getMixedColorPalette(theme.palette.primary.main);
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'))

  useEffect(() => {
    if (searchParams.has('campaignId')) {
      const token = searchParams.get('campaignId')
      if (token) {
        setCampaignId(token)
      }
    }
  }, [])

  useEffect(() => {
    if (campaignId) {
      getOneEmailCampaign();
      if (reportPeriodContext.lastSelectedReportId) {
        analyticService.getPieData(reportPeriodContext.lastSelectedReportId, `?data=employeeSurvey&campaignId=${campaignId}`)
          .then(response => {
            setAnalytics(response)
          })
          .catch(err => console.log(err))
      }
    }
  }, [campaignId, reportPeriodContext.lastSelectedReportId])

  useEffect(() => {
    if (newSurvey) {
      const promises = newSurvey.distributionListId.map(list =>
        distributionListAddressService.getAddressesForDistributionList(list)
      )
      Promise.all(promises)
        .then(responses => {
          const consolidatedEmails = responses.flat().map(response => ({
            name: response.name,
            emailAddress: response.emailAddress
          }));
          setEmailAddresses(consolidatedEmails);
        })
        .catch(err => console.log(err));
    }
  }, [newSurvey])

  const getOneEmailCampaign = () => {
    emailCampaignService.getOneEmailCampaign(campaignId)
      .then(response => {
        setEmailCampaign(response)
        setNewSurvey({
          name: response.name,
          emailFrequency: response.emailFrequency,
          campaignType: response.campaignType,
          reminderAfterDays: response.reminderAfterDays,
          sendDate: response.sendDate,
          sendTime: response.sendTime,
          tokenExpiryDays: response.tokenExpiryDays,
          reportPeriodId: response.reportPeriodId,
          includeProjects: response.includeProjects,
          distributionListId: response.distributionLists.map(list => list.id),
        })
      })
      .catch(err => console.log(err))
  }

  const changeHandler = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const submitSchedule = (values?: Partial<IPostEmailCampaign>) => {
    if (isReportPeriodLocked) {
      userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and cannot be modified`, buttonCopy: 'Ok', hideCancelButton: true })
    } else {
      if (newSurvey) {
        const formattedValues = {
          ...values,
          distributionListId: newSurvey.distributionListId,
        }
        if (!isEditPageFormSubmitting) {
          setIsEditPageFormSubmitting(true)
          emailCampaignService.editEmailCampaign(campaignId, formattedValues)
            .then((response) => {
              userNotificationsContext.openSnackbar({ open: true, message: 'Updated Successfully' })
              setIsEditPageFormSubmitting(false)
            })
            .catch(err => {
              if (err.response.data.code === 1) {
                userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' });
              } else {
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
              }
              console.log(err)
              setIsEditPageFormSubmitting(false)

            })
        }
      }
    }
  }
  const submitRecipients = (values: string[]) => {
    if (isReportPeriodLocked) {
      userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and cannot be modified`, buttonCopy: 'Ok', hideCancelButton: true })
    } else {
      if (newSurvey) {
        const formattedValues = {
          distributionListId: values,
          name: newSurvey.name,
          emailFrequency: newSurvey.emailFrequency,
          campaignType: newSurvey.campaignType,
          reminderAfterDays: newSurvey.reminderAfterDays,
          sendDate: newSurvey.sendDate,
          sendTime: newSurvey.sendTime,
          tokenExpiryDays: newSurvey.tokenExpiryDays,
          reportPeriodId: newSurvey.reportPeriodId,
          includeProjects: newSurvey.includeProjects,
        }
        if (!isEditPageFormSubmitting) {
          setIsEditPageFormSubmitting(true)
          emailCampaignService.editEmailCampaign(campaignId, formattedValues)
            .then((response) => {
              userNotificationsContext.openSnackbar({ open: true, message: 'Updated Successfully' })
              setIsEditPageFormSubmitting(false)
            })
            .catch(err => {
              if (err.response.data.code === 1) {
                userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' });
              } else {
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
              }
              console.log(err)
              setIsEditPageFormSubmitting(false)
            })
        }
      }
    }
  }

  const deactivateEmailCampaignHandler = () => {
    if (isReportPeriodLocked) {
      userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and cannot be modified`, buttonCopy: 'Ok', hideCancelButton: true })
    } else {
      userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: `Are you sure you want to deactivate this survey? This action cannot be undone.`, buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
        .then(response => {
          if (response) {
            if (!isChangingStatus) {
              setIsChangingStatus(true)
              emailCampaignService.deactivateEmailCampaign(campaignId)
                .then(response => {
                  navigate('/emissions/surveys/employee');
                  setIsChangingStatus(false)
                  userNotificationsContext.openSnackbar({ open: true, message: 'Deactivated successfully' });
                })
                .catch(err => {
                  if (err.response.data.code === 1) {
                    userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' });
                  } else {
                    userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                  }
                  setIsChangingStatus(false)
                  console.log(err)
                })
            }
          }
        })
    }
  }

  const activateEmailCampaignHandler = () => {
    if (isReportPeriodLocked) {
      userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and cannot be modified`, buttonCopy: 'Ok', hideCancelButton: true })
    } else {
      userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: `Are you sure you want to activate this survey?`, buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
        .then(response => {
          if (response) {
            if (!isChangingStatus) {
              setIsChangingStatus(true)
              emailCampaignService.activateEmailCampaign(campaignId)
                .then(response => {
                  if (campaignId) {
                    getOneEmailCampaign();
                  }
                  userNotificationsContext.openSnackbar({ open: true, message: 'Activated successfully' });
                  setIsChangingStatus(false)
                })
                .catch(err => {
                  if (err.response.data.code === 1) {
                    userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' });
                  } else {
                    userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                  }
                  setIsChangingStatus(false)
                  console.log(err)
                })
            }
          }
        })
    }
  }

  return (
    <PageOuterContainer>
      {id && emailCampaign &&
        <PageHeading
          title={`${id.charAt(0).toUpperCase() + id.slice(1).toLowerCase()} Survey Overview`}
          includeBackButton={true}
          backButtonLink={`/emissions/surveys/${id}`}
          backButtonText={`Back to ${id} surveys`}
          hasTabs={true}
          showChip={emailCampaign.active}
          chipColor={emailCampaign.active ? 'success' : 'default'}
          chipLabel={emailCampaign.active ? 'Active' : 'Pending'}
          includeActionButton={true}
          makeActionButtonLoading={true}
          actionButtonIcon={emailCampaign.active ? IconName.HideSourceOutlined : IconName.Check}
          actionButtonTextLoading={emailCampaign.active ? 'Deactivating Survey' : 'Activating Survey'}
          actionButtonTextNotLoading={emailCampaign.active ? 'Deactivate Survey' : 'Activate Survey'}
          actionButtonHandler={emailCampaign.active ? () => deactivateEmailCampaignHandler() : () => activateEmailCampaignHandler()}
          hideShowActionButton={isReportPeriodLocked === true}
          loadingStatus={isChangingStatus}
          actionButtonVariant='outlined'
        />}
      {emailCampaign &&
        <>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'lightGrey.main' }}>
              <Tabs value={value} onChange={changeHandler}  >
                <Tab label='Analytics' {...a11yProps(0)} sx={{ textTransform: 'capitalize', borderRadius: 1 }} />
                <Tab label='Schedule' {...a11yProps(1)} sx={{ textTransform: 'capitalize', borderRadius: 1 }} />
                <Tab label='Content' {...a11yProps(2)} sx={{ textTransform: 'capitalize', borderRadius: 1 }} />
                <Tab label='Recipients' {...a11yProps(3)} sx={{ textTransform: 'capitalize', borderRadius: 1 }} />
                {!isReportPeriodLocked && <Tab label='Review' {...a11yProps(4)} sx={{ textTransform: 'capitalize', borderRadius: 1 }} />}
              </Tabs>
            </Box>
          </Box>
          <CustomTabPanel value={value} index={0}>
            {!emailCampaign.active &&
              <Stack sx={{ mb: 2 }}>
                <InformationBox
                  text='This survey is not active'
                  icon={IconName.CheckCircleOutlined}
                />
              </Stack>
            }
            {emailCampaign.active && emailCampaign.sentCount === 0 &&
              <Stack sx={{ mb: 2 }}>
                <InformationBox
                  text='No emails have been sent out for this survey yet'
                  icon={IconName.ErrorOutline}
                />
              </Stack>
            }
            {emailCampaign.sentCount > 0 &&
              <Box sx={{ width: '100%', mb: 2 }}>
                <Typography variant='subtitle1' sx={{ mb: 1 }}>Survey Responses: {emailCampaign.completedCount}</Typography>
              </Box>}
            {analytics &&
              <Grid container spacing={2}>
                {analytics.data.transportMethodPie.length > 0 &&
                  <Grid item xl={4} lg={4} md={12} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <GraphDonutChart
                      title='Transport Methods used by Employees' tooltip='A breakdown of the different transportation methods used for employee commutes.'
                      dataset={analytics.data.transportMethodPie} />
                  </Grid>}
                {analytics.data.transportMethodGraph.length > 0 &&
                  <Grid item xl={8} lg={8} md={12} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <GraphBarChart title='Emission Breakdown per Transport Method' tooltip='The emission breakdown per transport method'
                      dataset={analytics.data.transportMethodGraph}
                      series={SeriesBar}
                      dataKey='transportMethodName'
                      layout='vertical'
                      colors={colors}
                    />
                  </Grid>
                }
              </Grid>
            }
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            {newSurvey &&
              <EmissionsEmailCampaignScheduleForm
                isEditPageSubmitting={isEditPageFormSubmitting}
                newSurvey={newSurvey}
                setNewSurvey={setNewSurvey}
                isEditPage={true}
                submitSchedule={submitSchedule}
              />}
          </CustomTabPanel>
          <CustomTabPanel value={value} index={2}>
            {newSurvey &&
              <EmissionsEmailCampaignContentForm
                isEditPageSubmitting={isEditPageFormSubmitting}
                newSurvey={newSurvey}
                setNewSurvey={setNewSurvey}
                isEditPage={true}
                submitSchedule={submitSchedule}
              />}
          </CustomTabPanel>
          <CustomTabPanel value={value} index={3}>
            {newSurvey &&
              <EmissionsEmailCampaignRecipientsForm
                newSurvey={newSurvey}
                isEditPageSubmitting={isEditPageFormSubmitting}
                setNewSurvey={setNewSurvey}
                getOneEmailCampaign={getOneEmailCampaign}
                campaignId={campaignId}
                isEditPage={true}
                handleNext={submitRecipients}
                isSubmitted={isSubmitted}
                setIsSubmitted={setIsSubmitted}
              />}
            {emailAddresses && <Box sx={{ borderWidth: '1px', borderStyle: 'solid', borderColor: 'lightGrey.main', borderRadius: '10px', mt: 2, p: 2 }}>
              {emailAddresses.map((email, index) => {
                return (
                  <Typography key={index} sx={{ color: '#00000061', pl: isMobileMode ? '14px' : '22px' }}>{email.emailAddress}</Typography>
                )
              })}
            </Box>}
          </CustomTabPanel>
          <CustomTabPanel value={value} index={4}>
            <EmissionsEmailCampaignReviewForm />
            <Typography variant='subtitle1' sx={{ mt: 4 }}>Preview of Email</Typography>
            <Box sx={{ mt: 1, borderWidth: '1px', borderStyle: 'solid', borderColor: 'lightGrey.main', borderRadius: '10px', pl: isMobileMode ? 4 : 10, pr: isMobileMode ? 4 : 10, pt: isMobileMode ? 2 : 4, pb: isMobileMode ? 2 : 4 }}>
              <EmailCampaignEmployeeCommuteForm isDemonstrationPage={true} includeProjects={newSurvey?.includeProjects} />
            </Box>
          </CustomTabPanel>
        </>}
    </PageOuterContainer >
  )
}